import React from "react";
import {
  ArrowLeft,
  BookOpen,
  Scale,
  Brain,
  FileText,
  Clock,
  Home,
  LayoutDashboard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@core/components/ui/button";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { DocsViewer } from "./DocsViewer";
import { useLegalNavigation } from "@core/hooks/useLegalNavigation";
import type { ProductType } from "@core/config/products.config";

interface MarkdownDocPageProps {
  productType: ProductType;
  docPath: string;
  title?: string;
  subtitle?: string;
  icon?: "legal" | "docs" | "brain" | "clock";
}

// `theme` transmis à DocsViewer pour ses couleurs de lien/code — conservé
// pour compat d'API mais ignoré par DocsViewer (bug #1 : le titre de la
// page légale suivait la couleur du PRODUIT, jamais celle du thème visuel
// actif — voir DocsViewer.tsx pour le même correctif côté liens/code).
const themeMap: Record<ProductType, "blue" | "violet" | "emerald" | "amber"> = {
  date: "blue",
  form: "violet",
  availability: "emerald",
  quizz: "amber",
};

const badgeClass = "bg-primary/10 border-primary/20 text-primary" as const;
const iconClass = "text-primary" as const;

const iconMap = {
  legal: Scale,
  docs: BookOpen,
  brain: Brain,
  clock: Clock,
};

/**
 * Page wrapper réutilisable pour afficher un document markdown
 * avec ProductHeader + ProductSidebar dans le layout standard.
 * Style aligné avec les pages Documentation standalone.
 */
export const MarkdownDocPage: React.FC<MarkdownDocPageProps> = ({
  productType,
  docPath,
  title,
  subtitle,
  icon = "docs",
}) => {
  const navigate = useNavigate();
  const navigation = useLegalNavigation(productType);
  const IconComponent = iconMap[icon] || BookOpen;

  return (
    <div className="flex flex-col min-h-screen-safe bg-background text-foreground">
      <ProductHeader productType={productType} showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType={productType} />
        <div className="flex-1">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Header */}
            <div className="text-center mb-12">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${badgeClass}`}
              >
                <IconComponent className={`w-4 h-4 ${iconClass}`} />
                <span className="text-sm">{title || "Document"}</span>
              </div>
              {title && (
                <h1 className="text-4xl font-extrabold tracking-tight mb-4 text-primary">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Content Card */}
            <div className="bg-card border border-border rounded-xl p-8 md:p-12">
              <DocsViewer docPath={docPath} theme={themeMap[productType]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
